import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Eye, Filter, RefreshCw, RotateCcw, Search } from 'lucide-react';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { EmptyState } from '../components/ui/empty-state';
import { LoadingState } from '../components/ui/loading-state';
import { Modal } from '../components/ui/modal';
import { PageHeader } from '../components/ui/page-header';
import { Select } from '../components/ui/select';
import { StatusBadge } from '../components/ui/status-badge';
import { OperationsCheckupDetailsDto, OperationsCheckupDto, TenantDto } from '../models';
import { getApiError, getOperationsCheckup, getOperationsCheckups, getTenants, requeueOperationsCheckup } from '../services/api';

const statusVariant = (status: number) => {
  if (status === 3) return 'success' as const;
  if (status === 4) return 'warning' as const;
  if (status === 5) return 'destructive' as const;
  if (status === 2) return 'primary' as const;
  return 'default' as const;
};

const resultLabel = (value?: number) => ({ 0: 'Не определён', 1: 'Новый', 2: 'Допуск', 3: 'Недопуск', 4: 'Повторный' }[value ?? 0] || '—');
const typeLabel = (value?: number) => ({ 1: 'Предрейсовый', 2: 'Послерейсовый', 3: 'Предсменный', 4: 'Послесменный' }[value ?? 0] || `Тип ${value ?? '—'}`);
const offerLabel = (value: number) => ({ 1: 'Предложено', 2: 'Принято', 3: 'Истекло', 4: 'Отменено' }[value] || String(value));
const formatDate = (value?: string) => value ? new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'medium' }).format(new Date(value)) : '—';

export const MedicalCheckupsPage = () => {
  const [items, setItems] = useState<OperationsCheckupDto[]>();
  const [tenants, setTenants] = useState<TenantDto[]>([]);
  const [status, setStatus] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [details, setDetails] = useState<OperationsCheckupDetailsDto>();
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [requeueCandidate, setRequeueCandidate] = useState<OperationsCheckupDto>();
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const response = await getOperationsCheckups({ status, tenantId, search: search.trim(), from, to });
      setItems(response.data.data);
    } catch (e) {
      setError(getApiError(e));
    }
  }, [from, search, status, tenantId, to]);

  useEffect(() => { getTenants().then((r) => setTenants(r.data.data)).catch(() => undefined); }, []);
  useEffect(() => { const timer = window.setTimeout(load, 200); return () => window.clearTimeout(timer); }, [load]);

  const openDetails = async (id: number) => {
    setDetailsLoading(true);
    setError('');
    try {
      const response = await getOperationsCheckup(id);
      setDetails(response.data.data);
    } catch (e) {
      setError(getApiError(e));
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleRequeue = async () => {
    if (!requeueCandidate) return;
    setSubmitting(true);
    setError('');
    try {
      await requeueOperationsCheckup(requeueCandidate.id);
      setSuccess(`Осмотр №${requeueCandidate.id} возвращён в основную очередь`);
      setRequeueCandidate(undefined);
      setDetails(undefined);
      await load();
    } catch (e) {
      setError(getApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const counts = useMemo(() => ({
    all: items?.length ?? 0,
    waiting: items?.filter((x) => x.status === 2).length ?? 0,
    errors: items?.filter((x) => x.status === 5).length ?? 0,
  }), [items]);

  return (
    <AppLayout title="Медосмотры">
      <PageHeader
        title="Медосмотры"
        description="Единый операционный список, состояние очереди, подписание и диагностические признаки."
        action={<Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4" /> Обновить</Button>}
      />

      {error && <div className="mb-4 rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
      {success && <div className="mb-4 rounded-xl border border-success/20 bg-success/10 px-4 py-3 text-sm text-success">{success}</div>}

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        {[
          { label: 'Показано', value: counts.all, tone: 'text-foreground' },
          { label: 'В очереди', value: counts.waiting, tone: 'text-primary' },
          { label: 'Ошибки подписи', value: counts.errors, tone: 'text-destructive' },
        ].map((item) => (
          <div key={item.label} className="rounded-2xl border border-border bg-card px-5 py-4 shadow-sm">
            <p className="text-xs font-medium text-muted-foreground">{item.label}</p>
            <p className={`mt-1 text-2xl font-bold ${item.tone}`}>{item.value}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <div className="grid gap-3 border-b border-border p-4 md:grid-cols-2 xl:grid-cols-[1.4fr_0.8fr_0.8fr_0.65fr_0.65fr]">
          <div className="flex h-10 items-center gap-2 rounded-lg border border-border bg-background px-3">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="ID, водитель или клиент" aria-label="Поиск медосмотров" className="w-full bg-transparent text-sm outline-none" />
          </div>
          <Select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Статус">
            <option value="">Все статусы</option>
            <option value="2">Новый</option>
            <option value="3">Вердикт вынесен</option>
            <option value="4">Подписание</option>
            <option value="5">Ошибка подписания</option>
          </Select>
          <Select value={tenantId} onChange={(e) => setTenantId(e.target.value)} aria-label="Организация">
            <option value="">Все организации</option>
            {tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name}</option>)}
          </Select>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="Дата с" className="h-10 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary" />
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="Дата по" className="h-10 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary" />
        </div>

        {!items && !error ? <LoadingState /> : items?.length === 0 ? (
          <EmptyState icon={<Filter className="h-8 w-8" />} title="Осмотры не найдены" description="Измените фильтры или диапазон дат." />
        ) : items ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1280px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">№</th>
                  <th className="px-4 py-3 font-semibold">Водитель / клиент</th>
                  <th className="px-4 py-3 font-semibold">Время</th>
                  <th className="px-4 py-3 font-semibold">Очередь</th>
                  <th className="px-4 py-3 font-semibold">Медик</th>
                  <th className="px-4 py-3 font-semibold">Результат</th>
                  <th className="px-4 py-3 font-semibold">Статус / ошибка</th>
                  <th className="px-4 py-3 text-right font-semibold">Действия</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-border/70 align-top transition-colors last:border-0 hover:bg-accent/30">
                    <td className="px-4 py-4 font-mono text-xs font-semibold">#{item.id}</td>
                    <td className="px-4 py-4">
                      <p className="font-semibold">{item.driver_name}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{item.company_name}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{typeLabel(item.type)}</p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-xs text-muted-foreground">{formatDate(item.started_at)}</td>
                    <td className="px-4 py-4">
                      <p className="font-medium">{item.queue_tenant_name || item.owner_tenant_name}</p>
                      {item.escalated_at && <p className="mt-1 text-xs text-warning">Был переброс</p>}
                    </td>
                    <td className="px-4 py-4">
                      <p>{item.medic_name || 'Не назначен'}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{item.medic_tenant_name || '—'}</p>
                    </td>
                    <td className="px-4 py-4 text-xs">
                      <p>Авто: <b>{resultLabel(item.auto_result)}</b></p>
                      <p className="mt-1">Медик: <b>{resultLabel(item.medic_result)}</b></p>
                    </td>
                    <td className="max-w-[260px] px-4 py-4">
                      <StatusBadge label={item.statusLabel} variant={statusVariant(item.status)} />
                      {item.issue && <p className="mt-2 flex gap-1.5 text-xs leading-5 text-destructive"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{item.issue}</p>}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex justify-end gap-2">
                        <Button size="icon-sm" variant="outline" title="Открыть детали" onClick={() => openDetails(item.id)}><Eye className="h-4 w-4" /></Button>
                        {[4, 5].includes(item.status) && <Button size="icon-sm" variant="destructive" title="Вернуть в очередь" onClick={() => setRequeueCandidate(item)}><RotateCcw className="h-4 w-4" /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <Modal open={Boolean(details) || detailsLoading} onClose={() => setDetails(undefined)} title={details ? `Медосмотр №${details.id}` : 'Загрузка...'} maxWidth="max-w-5xl"
        footer={details && [4, 5].includes(details.status) ? <Button variant="destructive" onClick={() => setRequeueCandidate(details)}><RotateCcw className="h-4 w-4" /> Вернуть в очередь</Button> : undefined}
      >
        {detailsLoading ? <LoadingState /> : details && (
          <div className="space-y-6">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ['Статус', details.statusLabel],
                ['Водитель', details.driver_name],
                ['Клиент', details.company_name],
                ['Очередь', details.queue_tenant_name || details.owner_tenant_name],
                ['Медик', details.medic_name || 'Не назначен'],
                ['Медорганизация', details.medic_tenant_name || '—'],
                ['Начало', formatDate(details.started_at)],
                ['Подписан', formatDate(details.signed_date)],
              ].map(([label, value]) => <div key={label} className="rounded-xl border border-border bg-muted/30 p-3"><p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p><p className="mt-1 text-sm font-semibold">{value}</p></div>)}
            </div>
            <div>
              <h3 className="mb-3 text-sm font-bold">Маршрут очереди</h3>
              {details.offers?.length ? <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full min-w-[720px] text-sm">
                  <thead><tr className="bg-muted/40 text-left text-xs text-muted-foreground"><th className="px-3 py-2">Попытка</th><th className="px-3 py-2">Организация</th><th className="px-3 py-2">Медик</th><th className="px-3 py-2">Статус</th><th className="px-3 py-2">Предложено</th></tr></thead>
                  <tbody>{details.offers.map((offer) => <tr key={offer.id} className="border-t border-border"><td className="px-3 py-3">#{offer.attempt_number}</td><td className="px-3 py-3">{offer.tenant_name}</td><td className="px-3 py-3">{offer.medic_name}</td><td className="px-3 py-3">{offerLabel(offer.status)}</td><td className="px-3 py-3 text-xs text-muted-foreground">{formatDate(offer.offered_at)}</td></tr>)}</tbody>
                </table>
              </div> : <p className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">Предложений медикам пока нет.</p>}
            </div>
            <div>
              <h3 className="mb-3 text-sm font-bold">Технические данные</h3>
              <div className="grid gap-2 text-xs sm:grid-cols-2">
                {[
                  ['Фото S3', details.photo_s3key],
                  ['Видео 1 S3', details.video_s3key],
                  ['Видео 2 S3', details.video2s3key],
                  ['JSON подписи', details.json_s3key],
                  ['Хеш', details.hash_s3key],
                  ['ЭЦП', details.signature_s3key],
                ].map(([label, value]) => <div key={label} className="flex min-w-0 gap-2 rounded-lg bg-muted/35 px-3 py-2"><span className="shrink-0 font-semibold">{label}:</span><span className="truncate text-muted-foreground">{value || 'нет'}</span></div>)}
              </div>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={Boolean(requeueCandidate)} onClose={() => setRequeueCandidate(undefined)} title="Вернуть осмотр в очередь" maxWidth="max-w-lg"
        footer={<><Button variant="ghost" disabled={submitting} onClick={() => setRequeueCandidate(undefined)}>Отмена</Button><Button variant="destructive" disabled={submitting} onClick={handleRequeue}>{submitting ? 'Возврат...' : 'Вернуть медикам'}</Button></>}
      >
        <div className="flex gap-3 rounded-xl border border-warning/25 bg-warning/10 p-4 text-sm">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
          <p>Осмотр №{requeueCandidate?.id} получит статус «Новый». Незавершённая подпись и старые технические предложения будут очищены, а измерения, фото и видео сохранятся.</p>
        </div>
      </Modal>
    </AppLayout>
  );
};
